import React, { useState } from "react"
import { useQuery, useQueryClient } from "react-query"
import { AxiosError } from "axios"
import moment from "moment"
import { IPersonnelDdcModel } from "@common-models/*"
import {
     CONST_PERSONNEL_DDC_SUIVI_TYPE_ACCEPTATION_DEMANDE,
     CONST_PERSONNEL_DDC_SUIVI_TYPE_ANNULATION_DEMANDE,
     CONST_PERSONNEL_DDC_SUIVI_TYPE_ENVOI_DEMANDE,
     CONST_PERSONNEL_DDC_SUIVI_TYPE_RECTIFICATION_AVANT_DECISION_RESPONSABLE,
     CONST_PERSONNEL_DDC_SUIVI_TYPE_REJET_DEMANDE,
     CONST_PERSONNEL_DDC_TYPE_ABSENCE_EXCEPTIONNELLE,
} from "@common-constants/*"
import MyAlert from "@common-utils/MyAlert"
import MySimpleSpinner from "@common-utils/MySimpleSpinner"
import MyModal from "@common-utils/MyModal"
import MyTooltip from "@common-utils/MyTooltip"
import GlobalHelper from "@common-helpers/GlobalHelper"
import useDateHelper from "@common-helpers/useDateHelper"
import MyGoogleDriveFileViewerInModal from "../../../../components/MyDocumentViewer/MyGoogleDriveFileViewerInModal"
import { getPersonnelDdcRequest } from "../../core/_requests"
import { useDdcActions } from "../../declaration/ddc/listing-multiple-personnel/core/_hooks"
import PersonnelDdcSuivi from "../../declaration/ddc/suivi/_PersonnelDdcSuivi"
import ActiviteCard from "./_ActiviteCard"

const REACT_QUERY_KEY = "REACT_QUERY_KEY_GET_ACTIVITE_DDC"

// Section « Demandes de congés » de l'onglet Activité. Triée par date de début décroissante (ordre garanti
// côté API), avec date de création, état cliquable (suivi) et actions Valider / Rejeter pour le responsable.
const DdcSection = ({ personnelId, canValider }: { personnelId: number; canValider: boolean }) => {
     const queryClient = useQueryClient()
     const { countWeekdaysBetweenDates } = useDateHelper()
     const ddcActions = useDdcActions()
     const [suiviInModal, setSuiviInModal] = useState<IPersonnelDdcModel>()
     const [justificatifInModal, setJustificatifInModal] = useState<IPersonnelDdcModel>()

     const query = useQuery<IPersonnelDdcModel[], AxiosError>(REACT_QUERY_KEY, () => getPersonnelDdcRequest(personnelId, {}).then(r => r.data))

     function handleActionCallback(updated: IPersonnelDdcModel) {
          queryClient.setQueryData<IPersonnelDdcModel[] | undefined>(REACT_QUERY_KEY, (prev: IPersonnelDdcModel[] | undefined) => {
               if (prev) {
                    const index = prev.findIndex(i => i.id === updated.id)
                    if (index >= 0) prev[index] = { ...prev[index], ...updated }
               }
               return prev
          })
     }

     function getDureeLabel(item: IPersonnelDdcModel): string {
          const totalDemiJournees = item.demiJournees?.length || 0
          const duree = countWeekdaysBetweenDates(item.du as moment.Moment, item.au as moment.Moment) - totalDemiJournees * 0.5
          return `${duree} ${duree > 1 ? "jours" : "jour"}`
     }

     function Badge({ item }: { item: IPersonnelDdcModel }) {
          const type = item.etat?.type as string
          let className = "badge-light-warning"
          let label = "En cours"
          if (type === CONST_PERSONNEL_DDC_SUIVI_TYPE_ACCEPTATION_DEMANDE) {
               className = "badge-light-success"
               label = "Validé"
          } else if (type === CONST_PERSONNEL_DDC_SUIVI_TYPE_REJET_DEMANDE) {
               className = "badge-light-danger"
               label = "Rejeté"
          } else if (type === CONST_PERSONNEL_DDC_SUIVI_TYPE_ANNULATION_DEMANDE) {
               className = "badge-secondary"
               label = "Annulé"
          }

          return (
               <MyTooltip title={"Voir le suivi"}>
                    <span className={`badge ${className} cursor-pointer`} onClick={() => setSuiviInModal(item)}>
                         {label}
                    </span>
               </MyTooltip>
          )
     }

     function Actions({ item }: { item: IPersonnelDdcModel }) {
          if (!canValider) return null
          const type = item.etat?.type as string
          const enCours = [CONST_PERSONNEL_DDC_SUIVI_TYPE_ENVOI_DEMANDE, CONST_PERSONNEL_DDC_SUIVI_TYPE_RECTIFICATION_AVANT_DECISION_RESPONSABLE].includes(type)
          const validee = type === CONST_PERSONNEL_DDC_SUIVI_TYPE_ACCEPTATION_DEMANDE

          if (!enCours && !validee) return null
          return (
               <>
                    {enCours && (
                         <MyTooltip title={"Valider cette demande"}>
                              <button
                                   type={"button"}
                                   className={"btn btn-sm btn-icon btn-light-success"}
                                   onClick={() => ddcActions.validation(item.id as number, handleActionCallback)}
                              >
                                   <i className={"fas fa-thumbs-up"} />
                              </button>
                         </MyTooltip>
                    )}
                    <MyTooltip title={"Rejeter cette demande"}>
                         <button type={"button"} className={"btn btn-sm btn-icon btn-light-danger"} onClick={() => ddcActions.rejet(item.id as number, handleActionCallback)}>
                              <i className={"fas fa-thumbs-down"} />
                         </button>
                    </MyTooltip>
               </>
          )
     }

     if (query.isFetching)
          return (
               <div className={"text-center py-4"}>
                    <MySimpleSpinner size={"xl"} />
               </div>
          )

     if (query.isError)
          return (
               <MyAlert type={"danger"}>
                    <div className={"d-flex align-items-center"}>
                         <div className={"me-2"}>{query.error?.response?.data?.detail}</div>
                         <button className={"btn btn-sm btn-danger p-0 px-3 py-2"} onClick={() => query.refetch()}>
                              Recharger
                         </button>
                    </div>
               </MyAlert>
          )

     if (!query.data || query.data.length === 0) return <MyAlert type={"primary"}>Aucune demande de congés n'a été déclarée.</MyAlert>

     return (
          <>
               <div className={"d-flex flex-column gap-3"}>
                    {query.data.map((item, key) => (
                         <ActiviteCard
                              key={key}
                              color={"warning"}
                              icon={"fa-umbrella-beach"}
                              title={item.type === CONST_PERSONNEL_DDC_TYPE_ABSENCE_EXCEPTIONNELLE ? item.sousTypeLabel : item.typeLabel}
                              badge={<Badge item={item} />}
                              periodeLabel={`${item.du?.parseZone().format("DD/MM/YYYY")} → ${item.au?.parseZone().format("DD/MM/YYYY")}`}
                              periodeTooltip={
                                   <span>
                                        Du {GlobalHelper.firstLetterUppercase(item.du?.parseZone().format("dddd D MMMM YYYY"))} au{" "}
                                        {GlobalHelper.firstLetterUppercase(item.au?.parseZone().format("dddd D MMMM YYYY"))}
                                        {item.demiJournees && item.demiJournees.length > 0 && (
                                             <>
                                                  <div className={"fw-bold mt-2"}>
                                                       Dont {item.demiJournees.length} demi-journée{item.demiJournees.length > 1 && "s"}
                                                  </div>
                                             </>
                                        )}
                                   </span>
                              }
                              dureeLabel={getDureeLabel(item)}
                              createdAtLabel={item.createdAt?.parseZone().format("DD/MM/YYYY")}
                              onViewJustificatif={item.justificatifGoogleDriveID ? () => setJustificatifInModal(item) : undefined}
                              actions={<Actions item={item} />}
                         />
                    ))}
               </div>

               {suiviInModal && (
                    <MyModal title={<span>{suiviInModal.typeLabel}</span>} show={true} onCloseCallback={() => setSuiviInModal(undefined)}>
                         <PersonnelDdcSuivi ddc={suiviInModal} />
                    </MyModal>
               )}

               {justificatifInModal && (
                    <MyGoogleDriveFileViewerInModal
                         googleDriveID={justificatifInModal.justificatifGoogleDriveID as string}
                         googleDriveMimetype={justificatifInModal.justificatifGoogleDriveMimeType as string}
                         modalTitle={<>Justificatif</>}
                         handleCloseModal={() => setJustificatifInModal(undefined)}
                         modalSize={"lg"}
                    />
               )}
          </>
     )
}

export default DdcSection
